import { useState, useEffect, useCallback } from 'react'
import { Loader2, Check, X, Play, Download, Building2, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import {
  fetchCompanyPayoutRequests,
  approveCompanyPayout,
  rejectCompanyPayout,
  processCompanyPayout,
} from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'
import { money } from '../utils/money'
import { payoutNote } from '../utils/payoutNote'

/**
 * Reviewing payouts owed to companies, rather than to individual collectors.
 *
 * Deliberately a separate list from the collector payouts beside it: the two
 * rails pay different parties, and merging them into one table would make it
 * easy to approve the wrong kind.
 */

interface CompanyPayoutRow {
  id: string
  amount: number
  currency: string
  method: string
  status: string
  adminNote: string | null
  failureReason: string | null
  createdAt: string
  processedAt: string | null
  completedAt: string | null
  /** What to look up in Paystack once the transfer has been sent. */
  paystackReference?: string | null
  company: { id: string; name: string; commissionRate: number }
  account: { accountName: string; bankName: string | null; isVerified: boolean }
}

const STATUS_TABS = ['PENDING', 'APPROVED', 'PROCESSING', 'COMPLETED', 'REJECTED', 'FAILED', 'ALL']

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PROCESSING: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
  FAILED: 'bg-red-100 text-red-700',
}

const METHOD_LABELS: Record<string, string> = {
  MTN_MOMO: 'MTN MoMo',
  TELECEL_CASH: 'Telecel Cash',
  AIRTELTIGO_MONEY: 'AirtelTigo',
  BANK_TRANSFER: 'Bank Transfer',
}


export default function AdminCompanyPayouts() {
  const [requests, setRequests] = useState<CompanyPayoutRow[]>([])
  const [counts, setCounts] = useState<Record<string, { count: number; amount: number }>>({})
  const [status, setStatus] = useState('PENDING')
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchCompanyPayoutRequests({ status, limit: 100 })
      setRequests(data?.requests ?? [])
      setCounts(data?.counts ?? {})
    } catch {
      toast.error('Failed to load company payouts')
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  const act = async (id: string, action: 'approve' | 'reject' | 'process' | 'check') => {
    setBusyId(id)
    try {
      if (action === 'approve') {
        await approveCompanyPayout(id)
        toast.success('Payout approved')
      } else if (action === 'reject') {
        const reason = prompt('Why is this payout being rejected?')
        if (!reason || reason.trim().length < 3) {
          if (reason !== null) toast.error('Give a reason of at least 3 characters')
          return
        }
        await rejectCompanyPayout(id, reason.trim())
        toast.success('Payout rejected')
      } else if (action === 'process') {
        const result = await processCompanyPayout(id)
        if (result?.needsOtp) {
          // Paystack holds the transfer until someone enters the OTP. A green
          // "initiated" said the money was on its way when it was not.
          toast(
            `Paystack is holding this transfer for an OTP. Finalise it in the Paystack dashboard. Reference: ${result.reference}`,
            { icon: '⚠️', duration: 12000 },
          )
        } else {
          toast.success('Transfer initiated')
        }
      } else {
        // Settles a payout stuck in processing from Paystack's own record.
        const res = await api.post(`/admin/company-payouts/${id}/check`, undefined, { timeout: 60000 })
        toast(res.data?.message ?? 'Checked with Paystack', { duration: 8000 })
      }
      load()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Action failed')
      // A failed send can still have changed the row (left processing).
      load()
    } finally {
      setBusyId(null)
    }
  }

  const pendingAmount = counts.PENDING?.amount ?? 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {STATUS_TABS.map((value) => (
            <button
              key={value}
              onClick={() => setStatus(value)}
              className={`px-3 py-2 text-xs font-semibold transition-colors ${
                status === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {value.charAt(0) + value.slice(1).toLowerCase()}
              {counts[value]?.count ? ` (${counts[value].count})` : ''}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {pendingAmount > 0 && (
            <span className="text-xs font-semibold text-yellow-700 bg-yellow-50 border border-yellow-200 px-3 py-1.5 rounded-xl">
              {money(pendingAmount)} awaiting review
            </span>
          )}
          <button
            type="button"
            onClick={() => {
              if (!requests.length) return
              exportToCsv(
                ['Company', 'Amount', 'Method', 'Account', 'Status', 'Requested', 'Completed', 'Note'],
                requests.map((r) => [
                  r.company.name, String(r.amount), r.method, r.account.accountName,
                  r.status, r.createdAt, r.completedAt ?? '',
                  payoutNote(r) ?? '',
                ]),
                'company-payouts',
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-3 py-2 rounded-xl hover:bg-gray-200 transition"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : requests.length === 0 ? (
          <div className="py-12 text-center">
            <Building2 className="w-9 h-9 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-semibold text-[#6B7280]">No company payouts here</p>
            <p className="text-xs text-[#9CA3AF] mt-1">
              Only companies paid on the consolidated model appear in this list.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Company', 'Amount', 'Paid into', 'Requested', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-xs font-semibold text-[#1A1A2E]">{r.company.name}</p>
                      <p className="text-[10px] text-[#6B7280]">
                        {Math.round(r.company.commissionRate * 100)}% commission
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs font-bold text-[#1A1A2E] whitespace-nowrap">
                      {money(r.amount, r.currency)}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs text-[#1A1A2E]">{r.account.accountName}</p>
                      <p className="text-[10px] text-[#6B7280]">
                        {METHOD_LABELS[r.method] ?? r.method}
                        {r.account.isVerified ? '' : ' · unverified'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {r.status.charAt(0) + r.status.slice(1).toLowerCase()}
                      </span>
                      {payoutNote(r) && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1 max-w-[160px] truncate">
                          {payoutNote(r)}
                        </p>
                      )}
                      {r.paystackReference && (
                        <p className="text-[10px] text-[#6B7280] mt-0.5 font-mono break-all">Ref {r.paystackReference}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {r.status === 'PENDING' && (
                          <>
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => act(r.id, 'approve')}
                              className="flex items-center gap-1 text-xs text-green-700 hover:bg-green-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Approve
                            </button>
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => act(r.id, 'reject')}
                              className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <X className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </>
                        )}
                        {r.status === 'APPROVED' && (
                          <>
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => act(r.id, 'process')}
                              className="flex items-center gap-1 text-xs text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <Play className="w-3.5 h-3.5" />
                              Send money
                            </button>
                            {/* An approved payout that cannot be sent no longer holds its amount for good. */}
                            <button
                              type="button"
                              disabled={busyId === r.id}
                              onClick={() => act(r.id, 'reject')}
                              className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                            >
                              <X className="w-3.5 h-3.5" />
                              Reject
                            </button>
                          </>
                        )}
                        {/* Paystack is asked by reference, so without one the check can only fail. */}
                        {r.paystackReference && (r.status === 'PROCESSING' || r.status === 'APPROVED') && (
                          <button
                            type="button"
                            disabled={busyId === r.id}
                            onClick={() => act(r.id, 'check')}
                            title="Ask Paystack what happened to this transfer, and settle the payout from its answer"
                            className="flex items-center gap-1 text-xs text-purple-700 hover:bg-purple-50 px-2 py-1 rounded-lg transition disabled:opacity-50 whitespace-nowrap"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                            Check with Paystack
                          </button>
                        )}
                        {/* Left by older code, which saved the reference only after Paystack answered. */}
                        {r.status === 'PROCESSING' && !r.paystackReference && (
                          <p className="text-[10px] text-[#6B7280] max-w-[180px]">
                            No Paystack reference was saved. Find this transfer in the Paystack dashboard by amount and recipient.
                          </p>
                        )}
                        {!['PENDING', 'APPROVED', 'PROCESSING'].includes(r.status) && (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
